/* ============================================================
   Talention — SVPT embebido: método «Modelo Ministerio»

   Réplica de la Herramienta SVPT oficial (Herramienta_SVPT.xlsm, Ministerio
   de Trabajo / Instituto de las Mujeres, abril 2022). Pesos, niveles y
   tramos son los del Excel y no se editan: el objetivo es dar los mismos
   puntos y la misma agrupación puesto a puesto.

   Catálogo fijo y cálculo puro. Sin React y sin importar data.js, que es
   quien importa este módulo.
   ============================================================ */

import { TEXTOS_MINISTERIO } from "./ministerio-textos.js";

const CATEGORIAS_MINISTERIO = [
  { id: "A", nombre: "Naturaleza de las funciones o tareas", corto: "Funciones", peso: 0.4, icon: "briefcase", color: "#0a84ff", iconBg: "#dfeeff" },
  { id: "B", nombre: "Condiciones educativas", corto: "Educación", peso: 0.2, icon: "file-text", color: "#af52de", iconBg: "#f1e4fa" },
  { id: "C", nombre: "Condiciones profesionales y de formación", corto: "Formación", peso: 0.25, icon: "brain", color: "#ec7a15", iconBg: "#ffe9d1" },
  { id: "D", nombre: "Condiciones laborales y factores estrictamente relacionados con el desempeño", corto: "Condiciones", peso: 0.15, icon: "factory", color: "#34c759", iconBg: "#e3f8e8" },
];

/* Agrupación intermedia de la interfaz (Sistema_Puntos!C:F). No entra en el cálculo. */
const SUBGRUPOS_MINISTERIO = [
  { id: "A-polivalencia", categoria: "A", nombre: "Polivalencia" },
  { id: "A-esfuerzos", categoria: "A", nombre: "Esfuerzos" },
  { id: "A-responsabilidad", categoria: "A", nombre: "Responsabilidad y autonomía" },
  { id: "A-otros", categoria: "A", nombre: "Otros" },
  { id: "B-ensenanza", categoria: "B", nombre: "Enseñanza reglada" },
  { id: "C-conocimientos", categoria: "C", nombre: "Conocimientos y comprensión" },
  { id: "C-aptitudes", categoria: "C", nombre: "Aptitudes" },
  { id: "C-habilidades", categoria: "C", nombre: "Habilidades sociales" },
  { id: "C-otros", categoria: "C", nombre: "Otros" },
  { id: "D-entorno", categoria: "D", nombre: "Entorno" },
  { id: "D-organizativas", categoria: "D", nombre: "Condiciones organizativas" },
  { id: "D-otros", categoria: "D", nombre: "Otros" },
];

/* Ejes de las matrices: Sistema_Puntos!M46:R51 y M60:R65. */
const EJES_MINISTERIO = {
  esfuerzo: {
    eje1: { nombre: "Frecuencia", valores: ["De vez en cuando (hasta el 30% de la jornada semanal)", "Normalmente (más del 30 y hasta el 60% de la jornada semanal)", "Siempre o casi siempre (más del 60% de la jornada semanal)"] },
    eje2: { nombre: "Intensidad", valores: ["Débil", "Moderado", "Fuerte"] },
  },
  riesgo: {
    eje1: { nombre: "Probabilidad", valores: ["Poco probable", "Probable", "Muy probable"] },
    eje2: { nombre: "Severidad", valores: ["Poco grave", "Grave", "Muy grave"] },
  },
};

/* pesoEfectivo: producto de los porcentajes en cascada (Sistema_Puntos!N).
   divisor: nº de niveles con el que el Excel reparte ese peso (columna P).
   opciones: nivel más alto que se puede elegir (el 0, «No relevante», siempre existe). */
const sub = (codigo, nombre, subgrupo, pesoEfectivo, opciones, divisor, extra = {}) => ({
  codigo, nombre, categoria: codigo[0], subgrupo, pesoEfectivo, opciones, divisor,
  tipo: "niveles", otros: false, ...extra,
});
const matriz = (ejes) => ({ tipo: "matriz", ejes });

const SUBFACTORES_MINISTERIO = [
  sub("A.1", "Polivalencia o definición extensa de obligaciones", "A-polivalencia", 0.12, 5, 5),
  sub("A.2.1", "Posición continuada y posturas forzadas", "A-esfuerzos", 0.00576, 5, 5, matriz("esfuerzo")),
  sub("A.2.2", "Movimientos repetitivos", "A-esfuerzos", 0.00576, 5, 5, matriz("esfuerzo")),
  sub("A.2.3", "Esfuerzo visual", "A-esfuerzos", 0.00576, 5, 5, matriz("esfuerzo")),
  sub("A.2.4", "Esfuerzo auditivo", "A-esfuerzos", 0.00576, 5, 5, matriz("esfuerzo")),
  sub("A.2.5", "Otros tipos de esfuerzo físico", "A-esfuerzos", 0.00576, 5, 5, matriz("esfuerzo")),
  sub("A.3", "Esfuerzo mental", "A-esfuerzos", 0.0336, 5, 5, matriz("esfuerzo")),
  sub("A.4", "Esfuerzo emocional", "A-esfuerzos", 0.0336, 5, 5, matriz("esfuerzo")),
  sub("A.5.1", "Responsabilidad de organización y coordinación", "A-responsabilidad", 0.0246, 5, 5),
  // Errata oficial replicada a propósito: la hoja divide entre 5 niveles pero
  // solo ofrece 0-3, así que A.5.2 nunca pasa de 14,76 puntos (spec D3).
  sub("A.5.2", "Responsabilidad de supervisión de resultados y calidad", "A-responsabilidad", 0.0246, 3, 5, { nota: "La herramienta oficial divide entre 5 niveles pero solo ofrece 3; se replica tal cual." }),
  sub("A.6.1", "Responsabilidad sobre el bienestar de las personas", "A-responsabilidad", 0.02296, 3, 3),
  sub("A.6.2", "Responsabilidad económica", "A-responsabilidad", 0.01435, 3, 3),
  sub("A.6.3", "Responsabilidad sobre información confidencial", "A-responsabilidad", 0.02009, 3, 3),
  sub("A.7", "Autonomía", "A-responsabilidad", 0.0574, 3, 3),
  sub("A.8", "Otros", "A-otros", 0.02, 5, 5, { otros: true }),
  sub("B.1", "Enseñanza reglada", "B-ensenanza", 0.2, 7, 7),
  sub("C.1.1", "Procedimientos, materiales, equipos y máquinas", "C-conocimientos", 0.016875, 3, 3),
  sub("C.1.2", "Competencias digitales", "C-conocimientos", 0.016875, 3, 3),
  sub("C.1.3", "Gestión de la diversidad", "C-conocimientos", 0.01125, 3, 3),
  sub("C.1.4", "Conocimiento o dominio de idioma extranjero", "C-conocimientos", 0.0196875, 5, 5),
  sub("C.1.5", "Formación no reglada", "C-conocimientos", 0.0196875, 3, 3),
  sub("C.1.6", "Experiencia", "C-conocimientos", 0.01125, 5, 5),
  sub("C.1.7", "Actualización de conocimientos", "C-conocimientos", 0.016875, 3, 3),
  sub("C.2.1", "Destreza", "C-aptitudes", 0.015625, 3, 3),
  sub("C.2.2", "Minuciosidad", "C-aptitudes", 0.015625, 3, 3),
  sub("C.2.3", "Aptitudes sensoriales", "C-aptitudes", 0.015625, 3, 3),
  sub("C.2.4", "Capacidad para plantear ideas y soluciones", "C-aptitudes", 0.015625, 3, 3),
  sub("C.3.1", "Capacidad comunicativa", "C-habilidades", 0.021875, 3, 3),
  sub("C.3.2", "Capacidad emocional", "C-habilidades", 0.01875, 3, 3),
  sub("C.3.3", "Capacidad de resolución de conflictos", "C-habilidades", 0.021875, 3, 3),
  sub("C.4", "Otros", "C-otros", 0.0125, 5, 5, { otros: true }),
  sub("D.1.1", "Condiciones físicas", "D-entorno", 0.045, 5, 5, matriz("riesgo")),
  sub("D.1.2", "Condiciones psicosociales", "D-entorno", 0.045, 5, 5, matriz("riesgo")),
  sub("D.2.1", "Horarios, descansos y vacaciones", "D-organizativas", 0.02625, 5, 5),
  sub("D.2.2", "Desplazamientos y viajes", "D-organizativas", 0.02625, 5, 5),
  sub("D.3", "Otros", "D-otros", 0.0075, 5, 5, { otros: true }),
];

const CODIGOS_OTROS = ["A.8", "C.4", "D.3"];
const POR_CODIGO = new Map(SUBFACTORES_MINISTERIO.map(s => [s.codigo, s]));

/* Cortes de Agrupacion!E6:F15. Techo inclusivo. */
const TRAMOS_MINISTERIO = [
  { agrupacion: 1, techo: 200, color: "#94a3b8" },
  { agrupacion: 2, techo: 250, color: "#64748b" },
  { agrupacion: 3, techo: 300, color: "#0891b2" },
  { agrupacion: 4, techo: 400, color: "#0a84ff" },
  { agrupacion: 5, techo: 500, color: "#5856d6" },
  { agrupacion: 6, techo: 550, color: "#7c3aed" },
  { agrupacion: 7, techo: 650, color: "#af52de" },
  { agrupacion: 8, techo: 700, color: "#d97706" },
  { agrupacion: 9, techo: 780, color: "#ea580c" },
  { agrupacion: 10, techo: 1000, color: "#16a34a" },
];

const subfactorMinisterio = (codigo) => POR_CODIGO.get(codigo) || null;

const otroVacio = () => ({ activo: false, nombre: "", justificacion: "", niveles: ["", "", "", "", ""] });

function crearMinisterioVacio() {
  return { otros: Object.fromEntries(CODIGOS_OTROS.map(c => [c, otroVacio()])), respuestas: {} };
}

/* Un «Otros» solo puede estar activo con nombre y los 5 textos de nivel. */
function otroCompleto(otro) {
  return !!otro
    && typeof otro.nombre === "string" && otro.nombre.trim() !== ""
    && Array.isArray(otro.niveles) && otro.niveles.length === 5
    && otro.niveles.every(t => typeof t === "string" && t.trim() !== "");
}

/* Subfactores que puntúan: los 33 fijos y los «Otros» activos. */
function subfactoresAplicables(otros) {
  return SUBFACTORES_MINISTERIO.filter(s => !s.otros || otros?.[s.codigo]?.activo === true);
}

const ejeValido = (v) => Number.isInteger(v) && v >= 1 && v <= 3;

/* Nivel de una respuesta: el entero tal cual; en una matriz, eje1 + eje2 − 1
   (tabla oficial 1 2 3 / 2 3 4 / 3 4 5). Sin respuesta, o con un eje sin
   marcar, no hay nivel. */
function nivelDeRespuesta(subfactor, respuesta) {
  if (respuesta == null) return null;
  if (Number.isInteger(respuesta)) return respuesta;
  if (subfactor?.tipo === "matriz" && typeof respuesta === "object"
      && ejeValido(respuesta.eje1) && ejeValido(respuesta.eje2)) {
    return respuesta.eje1 + respuesta.eje2 - 1;
  }
  return null;
}

/* Lo que el reducer acepta y la poda conserva: un entero 0..opciones o, en
   una matriz, los dos ejes en 1..3. */
function respuestaValida(subfactor, respuesta) {
  if (!subfactor) return false;
  if (Number.isInteger(respuesta)) return respuesta >= 0 && respuesta <= subfactor.opciones;
  return subfactor.tipo === "matriz" && respuesta != null && typeof respuesta === "object"
    && ejeValido(respuesta.eje1) && ejeValido(respuesta.eje2);
}

/* Puntos de un subfactor: nivel × 1000 × pesoEfectivo / divisor. Sin redondeo. */
function puntosDeSubfactor(subfactor, respuesta) {
  const nivel = nivelDeRespuesta(subfactor, respuesta);
  if (nivel == null) return 0;
  return nivel * 1000 * subfactor.pesoEfectivo / subfactor.divisor;
}

function puntosMinisterio(respuestas, otros) {
  if (!respuestas) return 0;
  return subfactoresAplicables(otros)
    .reduce((s, sf) => s + puntosDeSubfactor(sf, respuestas[sf.codigo]), 0);
}

function puntosPorCategoria(respuestas, otros) {
  const out = Object.fromEntries(CATEGORIAS_MINISTERIO.map(c => [c.id, 0]));
  if (!respuestas) return out;
  for (const sf of subfactoresAplicables(otros)) out[sf.categoria] += puntosDeSubfactor(sf, respuestas[sf.codigo]);
  return out;
}

/* total: subfactores aplicables. done: los que tienen nivel (el 0 cuenta). */
function progresoMinisterio(respuestas, otros) {
  const aplicables = subfactoresAplicables(otros);
  const done = respuestas
    ? aplicables.filter(sf => nivelDeRespuesta(sf, respuestas[sf.codigo]) != null).length
    : 0;
  return { total: aplicables.length, done };
}

function estadoMinisterio(respuestas, otros) {
  const { total, done } = progresoMinisterio(respuestas, otros);
  if (total > 0 && done === total) return "FINALIZADA";
  if (done > 0) return "EN PROGRESO";
  return "PENDIENTE";
}

/* Margen que absorbe el ruido de coma flotante de la suma (una combinación
   que da exactamente 500 puede salir 500,00000000000006). Los puntos no se
   redondean: solo se comparan con esta holgura. */
const TOLERANCIA_PUNTOS = 1e-9;

/* Primer tramo cuyo techo cubre los puntos. Con 0 puntos no hay agrupación:
   el Excel deja la celda vacía. */
function agrupacionMinisterio(puntos) {
  if (!(puntos > TOLERANCIA_PUNTOS)) return null;
  return TRAMOS_MINISTERIO.find(t => puntos <= t.techo + TOLERANCIA_PUNTOS) || null;
}

function rangoDeTramo(tramo) {
  const i = TRAMOS_MINISTERIO.findIndex(t => t.agrupacion === tramo.agrupacion);
  return i === 0
    ? `Hasta ${tramo.techo} pts`
    : `Más de ${TRAMOS_MINISTERIO[i - 1].techo} y hasta ${tramo.techo} pts`;
}

/* Quita lo que no encaja en el catálogo: códigos inexistentes, valores
   fuera de rango, ejes fuera de 1..3 y respuestas a «Otros» inactivos. */
function podarRespuestasMinisterio(respuestas, otros) {
  const limpias = {};
  let removed = 0;
  for (const [positionId, porCodigo] of Object.entries(respuestas || {})) {
    const kept = {};
    for (const [codigo, respuesta] of Object.entries(porCodigo || {})) {
      const sf = subfactorMinisterio(codigo);
      const aplicable = sf && (!sf.otros || otros?.[codigo]?.activo === true);
      if (aplicable && respuestaValida(sf, respuesta)) kept[codigo] = respuesta;
      else removed++;
    }
    limpias[positionId] = kept;
  }
  return { respuestas: limpias, removed };
}

function textosDeSubfactor(codigo) {
  return TEXTOS_MINISTERIO[codigo] || null;
}

/* Puntos del informe y del detalle por subfactor. En el resto de la
   interfaz los puntos se redondean a entero. */
const conDosDecimales = (n) => n.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export {
  CATEGORIAS_MINISTERIO,
  SUBGRUPOS_MINISTERIO,
  SUBFACTORES_MINISTERIO,
  EJES_MINISTERIO,
  TRAMOS_MINISTERIO,
  CODIGOS_OTROS,
  subfactorMinisterio,
  subfactoresAplicables,
  crearMinisterioVacio,
  otroCompleto,
  nivelDeRespuesta,
  respuestaValida,
  puntosDeSubfactor,
  puntosMinisterio,
  puntosPorCategoria,
  progresoMinisterio,
  estadoMinisterio,
  TOLERANCIA_PUNTOS,
  agrupacionMinisterio,
  rangoDeTramo,
  podarRespuestasMinisterio,
  textosDeSubfactor,
  conDosDecimales,
};
